import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { TabConfiguration } from '@/types';

interface BookingTypeFieldOrder {
  bookingTypeId: string;
  fieldOrder: string[];
  tabOrder: TabConfiguration[];
  savedAt: string;
}

interface FieldOrderingContextType {
  getFieldOrder: (bookingTypeId: string) => { fieldOrder: string[]; tabOrder: TabConfiguration[] } | undefined;
  saveFieldOrder: (bookingTypeId: string, fieldOrder: string[], tabOrder: TabConfiguration[]) => void;
  clearFieldOrder: (bookingTypeId: string) => void;
  clearAllFieldOrders: () => void;
}

const FieldOrderingContext = createContext<FieldOrderingContextType | undefined>(undefined);

const STORAGE_KEY = 'booking_type_field_orders';

export function FieldOrderingProvider({ children }: { children: ReactNode }) {
  const [fieldOrders, setFieldOrders] = useState<BookingTypeFieldOrder[]>(() => {
    try {
      const stored = sessionStorage.getItem(STORAGE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error('Error loading field orders from session storage:', error);
      return [];
    }
  });

  // Persist to session storage whenever fieldOrders changes
  useEffect(() => {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(fieldOrders));
      console.log('💾 Field orders saved to session storage:', fieldOrders);
    } catch (error) {
      console.error('Error saving field orders to session storage:', error);
    }
  }, [fieldOrders]);

  const getFieldOrder = (bookingTypeId: string) => {
    const order = fieldOrders.find(fo => String(fo.bookingTypeId) === String(bookingTypeId));
    if (order) {
      console.log(`📖 Retrieved field order for booking type ${bookingTypeId}:`, order);
      return {
        fieldOrder: order.fieldOrder,
        tabOrder: order.tabOrder,
      };
    }
    console.log(`📖 No custom field order found for booking type ${bookingTypeId}`);
    return undefined;
  };

  const saveFieldOrder = (bookingTypeId: string, fieldOrder: string[], tabOrder: TabConfiguration[]) => {
    console.log(`💾 Saving field order for booking type ${bookingTypeId}:`, { fieldOrder, tabOrder });
    
    setFieldOrders(prev => {
      const existingIndex = prev.findIndex(fo => String(fo.bookingTypeId) === String(bookingTypeId));
      const newOrder: BookingTypeFieldOrder = {
        bookingTypeId,
        fieldOrder,
        tabOrder,
        savedAt: new Date().toISOString(),
      };

      if (existingIndex >= 0) {
        // Update existing
        const updated = [...prev];
        updated[existingIndex] = newOrder;
        return updated;
      }
      // Add new
      return [...prev, newOrder];
    });
  };

  const clearFieldOrder = (bookingTypeId: string) => {
    console.log(`🗑️ Clearing field order for booking type ${bookingTypeId}`);
    setFieldOrders(prev => prev.filter(fo => String(fo.bookingTypeId) !== String(bookingTypeId)));
  };

  const clearAllFieldOrders = () => {
    console.log('🗑️ Clearing all field orders');
    setFieldOrders([]);
  };

  return (
    <FieldOrderingContext.Provider
      value={{
        getFieldOrder,
        saveFieldOrder,
        clearFieldOrder,
        clearAllFieldOrders,
      }}
    >
      {children}
    </FieldOrderingContext.Provider>
  );
}

export function useFieldOrdering() {
  const context = useContext(FieldOrderingContext);
  if (context === undefined) {
    throw new Error('useFieldOrdering must be used within a FieldOrderingProvider');
  }
  return context;
}
